import { get } from 'lodash';

import { IApiResponse } from '@shared/types';

type TKeyMapping = {
	data?: string;
	pageSize?: string;
	pageNumber?: string;
	start?: string;
	offset?: string;
	totalCount?: string;
};

type IApiPayload = Partial<Record<string, string | number | object | boolean>>;

export interface IMakePaginatedApiProps<Data, Payload = IApiPayload> {
	apiCallback: (params: Payload) => Promise<IApiResponse<Data>>;
	payload: Payload;
	config: {
		// overrideStartWithLimit: To override the start of next API call with pageSize * limit, as getUsers API works with getting data from multiple DBs, so response size can be greater than page limit.
		overrideStartWithLimit?: boolean;
		keyMapping: TKeyMapping;
	};
}

export const makePaginatedApi = async <T, Payload = IApiPayload>(
	{ apiCallback, payload, config }: IMakePaginatedApiProps<T, Payload>,
	allData: Array<T> = [],
): Promise<Array<T>> => {
	const pageSize = get(
		payload,
		config.keyMapping.pageSize as string,
		50,
	) as number;
	const pageStart = get(payload, config.keyMapping.start as string) as number;
	const pageOffset = get(
		payload,
		config.keyMapping.offset as string,
		40,
	) as number;

	const pageNumber = get(
		payload,
		config.keyMapping.pageNumber as string,
	) as number;

	const apiResponse = await apiCallback(payload);
	const { data: response } = apiResponse;
	let data = response as T[];

	// config.keyMapping.data does not exist then use response
	if (config.keyMapping.data) {
		data = get(response, config.keyMapping.data, []) as T[];
	}

	const newData = [...allData, ...data];
	const totalCount = get(
		apiResponse,
		config.keyMapping.totalCount as string,
		0,
	);

	// Returning if fetching all data is done
	if (totalCount === newData.length || data.length < pageSize) {
		return newData;
	}

	// Continue to recursively call in case of more data
	const newApiPayload = {
		...payload,
	} as IApiPayload;

	if (config.keyMapping.start && pageStart >= 0) {
		newApiPayload[config.keyMapping.start] = newData.length + 1;
		if (config.overrideStartWithLimit) {
			newApiPayload[config.keyMapping.start] = pageStart + pageSize;
		}
	} else if (config.keyMapping.offset && pageOffset >= 0) {
		newApiPayload[config.keyMapping.offset] = newData.length;
	} else {
		newApiPayload[config.keyMapping.pageNumber!] = pageNumber + 1;
	}

	return makePaginatedApi(
		{
			apiCallback,
			payload: newApiPayload as Payload,
			config,
		},
		newData,
	);
};
